import type { Decision, Regime } from "../types";

type ConsentValue = "granted" | "denied";

interface ConsentSignals {
  ad_storage: ConsentValue;
  analytics_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  functionality_storage: ConsentValue;
  personalization_storage: ConsentValue;
  security_storage: ConsentValue;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Google Consent Mode v2. Mandatory for EEA traffic since March 2024, and since
 * 15 June 2026 `ad_storage` alone decides whether data reaches Google Ads — Google
 * Signals no longer factors in.
 *
 * The default push must happen *before* any Google tag loads, otherwise the tag runs
 * its own default (granted) first and the denial arrives too late to matter.
 *
 * The defaults are the regime baseline, not a constant: under the DSGVO that is everything
 * denied, under US law it is what the site enabled, minus anything a GPC signal already
 * took away. Pushing `denied` in the opt-out regime would suppress measurement the visitor
 * never objected to; pushing `granted` in the EU would be the violation.
 */
export function pushConsentDefault(baseline: Decision, regime: Regime): void {
  gtag("consent", "default", {
    ...signalsFor(baseline),
    // Google's own recommendation, and only meaningful where a decision is still to come:
    // hold tags briefly rather than let them fire under the denied default.
    ...(regime === "opt-in" ? { wait_for_update: 500 } : {}),
  });
  gtag("set", "ads_data_redaction", !baseline.marketing);
  gtag("set", "url_passthrough", true);
}

export function pushConsentUpdate(decision: Decision): void {
  gtag("consent", "update", signalsFor(decision));
  gtag("set", "ads_data_redaction", !decision.marketing);
}

function signalsFor(decision: Decision): ConsentSignals {
  return {
    ad_storage: flag(decision.marketing),
    ad_user_data: flag(decision.marketing),
    ad_personalization: flag(decision.marketing),
    analytics_storage: flag(decision.analytics),
    functionality_storage: flag(decision.functional),
    personalization_storage: flag(decision.functional),
    // Always granted: this covers authentication and fraud prevention, which no consent
    // model treats as optional.
    security_storage: "granted",
  };
}

function flag(granted: boolean): ConsentValue {
  return granted ? "granted" : "denied";
}

/**
 * Written the way Google's own snippet is: the `arguments` object must be pushed, not an
 * array literal. gtag.js identifies commands by that object's shape, and a plain array is
 * silently ignored — a mistake that looks like it works right up until consent data is
 * missing from reports.
 */
const gtag = function gtagShim(this: unknown): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(arguments);
} as (...args: unknown[]) => void;
